import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  dashboardBrainPartsStateDefault,
  DashboardBrainPartsStateModel,
  dashboardBrainPartsUniqueStateId,
} from './dashboard-brain-parts.model';
import {
  LoadDashboardBrainParts,
  LoadDashboardBrainPartsFail,
  LoadDashboardBrainPartsSuccess,
  ResetDashboardBrainPartsLoadingStatus,
  SelectDefaultBrainPart,
  SelectMainLevelBrainPart,
  SelectSublevelBrainPart,
} from './dashboard-brain-parts.actions';
import {DashboardBrainPartsService} from '../../services/dashboard-brain-parts/dashboard-brain-parts.service';
import {PROGRESS_STATUSES} from '../../../shared/symbols';
import {DashboardBrainPart, DashboardBrainPartSecondaryLevel} from '../../symbols/dashboard-brain-parts-general.symbols';
import {
  dashboardBrainPartsCreateParentMap,
  dashboardBrainPartsDefineLoadingPriority,
  dashboardBrainPartsDefineNearestPartWithChart,
  dashboardBrainPartsDefineRootParentId,
  dashboardBrainPartsFormFlatListMap,
  mergeBrainPartsData,
} from '../../symbols/dashboard-brain-parts-utils.symbols';
import {SubmissionState} from '../../../shared/microservices/submission/submission.state';
import {
  acceptableScanQualityThreshold,
  getScanQualityForScanType,
  getScanStatusForScanType,
  Submission,
  SubmissionDetailedStatus,
  SubmissionScanQuality,
} from '../../../dashboard/symbols/submission.symbols';
import {SubmissionScanImageType} from '../../../dashboard/symbols/general.symbols';
import {submissionDetailedStatusToUserMessage} from '../../../dashboard-shared/symbols/dashboard-shared-statuses.symbols';

@State<DashboardBrainPartsStateModel>({
  name: dashboardBrainPartsUniqueStateId,
  defaults: dashboardBrainPartsStateDefault,
})
@Injectable()
export class DashboardBrainPartsState {
  constructor(private dashboardBrainPartsService: DashboardBrainPartsService) {}

  @Selector()
  static loadBrainPartsStatus(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['loadBrainPartsStatus'] {
    return state.loadBrainPartsStatus;
  }

  /**
   * Stores all the present brain parts.
   * It is marked as unsafe, as it contains all the brain parts, including the ones that are not presented in the submission.
   * To get the safe list of brain parts, use the `brainPartList` selector from the `DashboardBrainPartsStateQueries` class.
   */
  @Selector()
  static brainPartListUnsafe(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['brainPartList'] {
    return state.brainPartList;
  }

  @Selector()
  static selectedMainLevelBrainPart(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['selectedMainLevelBrainPart'] {
    return state.selectedMainLevelBrainPart;
  }

  @Selector()
  static lastSelectedBrainPart(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['lastSelectedBrainPart'] {
    return state.lastSelectedBrainPart;
  }

  @Selector()
  static brainPartToShowInChart(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['brainPartToShowInChart'] {
    return state.brainPartToShowInChart;
  }

  @Selector()
  static brainPartsParentMap(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['brainPartsParentMap'] {
    return state.brainPartsParentMap;
  }

  @Selector()
  static brainPartsFlatListMap(state: DashboardBrainPartsStateModel): DashboardBrainPartsStateModel['brainPartsFlatListMap'] {
    return state.brainPartsFlatListMap;
  }

  @Selector()
  static brainPartSurfacesLoadingPriority(
    state: DashboardBrainPartsStateModel
  ): DashboardBrainPartsStateModel['brainPartSurfacesLoadingPriority'] {
    return state.brainPartSurfacesLoadingPriority;
  }

  @Action(LoadDashboardBrainParts)
  loadDashboardBrainParts(ctx: StateContext<DashboardBrainPartsStateModel>): void {
    ctx.patchState({
      loadBrainPartsStatus: PROGRESS_STATUSES.IN_PROGRESS,
      lastLoadBrainPartsError: null,
    });

    this.dashboardBrainPartsService.loadBrainParts().subscribe({
      next: brainParts => ctx.dispatch(new LoadDashboardBrainPartsSuccess(brainParts)),
      error: error => ctx.dispatch(new LoadDashboardBrainPartsFail(error)),
    });
  }

  @Action(LoadDashboardBrainPartsSuccess)
  loadDashboardBrainPartsSuccess(ctx: StateContext<DashboardBrainPartsStateModel>, {brainParts}: LoadDashboardBrainPartsSuccess): void {
    // Form a flat list map for the brain parts.
    const brainPartFlatListMap = dashboardBrainPartsFormFlatListMap(brainParts);

    ctx.patchState({
      loadBrainPartsStatus: PROGRESS_STATUSES.SUCCEED,
      brainPartList: brainParts,
      brainPartsParentMap: dashboardBrainPartsCreateParentMap(brainParts),
      brainPartsFlatListMap: brainPartFlatListMap,
      brainPartSurfacesLoadingPriority: dashboardBrainPartsDefineLoadingPriority(brainPartFlatListMap),
    });

    ctx.dispatch(new SelectDefaultBrainPart());
  }

  @Action(LoadDashboardBrainPartsFail)
  loadDashboardBrainPartsFail(ctx: StateContext<DashboardBrainPartsStateModel>, {error}: LoadDashboardBrainPartsFail): void {
    ctx.patchState({
      loadBrainPartsStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadBrainPartsError: error,
    });
  }

  @Action(SelectMainLevelBrainPart)
  selectDashboardBrainPart(ctx: StateContext<DashboardBrainPartsStateModel>, {brainPart}: SelectMainLevelBrainPart): void {
    ctx.patchState({
      selectedMainLevelBrainPart: brainPart,
      lastSelectedBrainPart: brainPart,

      // The main level brain part is always displayed in the chart when selected.
      brainPartToShowInChart: brainPart,
    });
  }

  @Action(SelectSublevelBrainPart)
  selectSublevelBrainPart(ctx: StateContext<DashboardBrainPartsStateModel>, {brainPart}: SelectSublevelBrainPart): void {
    const state = ctx.getState();

    // Find the root parent brain part id in the map.
    const rootParentBrainPartId = dashboardBrainPartsDefineRootParentId(brainPart.id, state.brainPartsParentMap);

    // Extract the root parent brain part from the flat list.
    // Assign the type manually, as the root parent means main level brain part, which is always a `DashboardBrainPart`.
    const rootParentBrainPart = state.brainPartsFlatListMap.get(rootParentBrainPartId) as DashboardBrainPart;

    // Merge the brain part data with the root parent brain part data to add data about view type and chart type.
    const updatedBrainPart = mergeBrainPartsData(rootParentBrainPart, brainPart);

    // Update the brain part with the about data from the root parent brain part if needed.
    const updatedBrainPartWithAbout = {
      ...updatedBrainPart,
      shortAbout: updatedBrainPart.useRootPartAbout ? rootParentBrainPart.shortAbout : brainPart.shortAbout,
      detailedAbout: updatedBrainPart.useRootPartAbout ? rootParentBrainPart.detailedAbout : brainPart.detailedAbout,
    }

    // Find the nearest brain part with chart.
    const nearestBrainPartWithChart = dashboardBrainPartsDefineNearestPartWithChart(
      brainPart,
      state.brainPartsParentMap,
      state.brainPartsFlatListMap
    );

    // Merge the brain part data with the root parent brain part data to add data about view type and chart type.
    const updatedNearestBrainPartWithChart = mergeBrainPartsData(rootParentBrainPart, nearestBrainPartWithChart);

    ctx.patchState({
      // Update the main level brain part with the root parent brain part.
      // Cover the cases if somehow the user selected a sublevel brain part from another brain part node.
      selectedMainLevelBrainPart: rootParentBrainPart,

      // Update the last selected brain part with a brain part of any level.
      lastSelectedBrainPart: updatedBrainPartWithAbout,

      // Update the brain part to display in the chart with a brain part that have a chart.
      brainPartToShowInChart: updatedNearestBrainPartWithChart,
    });
  }

  @Action(SelectDefaultBrainPart)
  selectDefaultBrainPart(ctx: StateContext<DashboardBrainPartsStateModel>): void {
    // Extract the brain part list from the state.
    const brainPartList = ctx.getState()?.brainPartList;

    // Ignore the action if the brain part list is empty.
    if (!brainPartList?.length) {
      return;
    }

    // Extract the first brain part from the list.
    const brainPart = brainPartList[0];

    // Update the state with the extracted brain part.
    ctx.patchState({
      selectedMainLevelBrainPart: brainPart,
      lastSelectedBrainPart: brainPart,

      // The main level brain part is always displayed in the chart when selected.
      brainPartToShowInChart: brainPart,
    });
  }

  @Action(ResetDashboardBrainPartsLoadingStatus)
  resetDashboardBrainPartsLoadingStatus(ctx: StateContext<DashboardBrainPartsStateModel>): void {
    ctx.patchState({
      loadBrainPartsStatus: dashboardBrainPartsStateDefault.loadBrainPartsStatus,
    });
  }
}

/**
 * Contains selectors for the dashboard brain parts state.
 * The selectors were moved to a separate class for optimization purposes.
 *
 * As all NGXS selectors are memoized, they only update when provided input changes.
 * As the typical state selectors take the whole state as input, they will update when any part of the state changes.
 *
 * By moving the selectors to a separate class, we can define the input for each selector.
 */
@Injectable()
export class DashboardBrainPartsStateQueries {
  /**
   * List of the submissions related to the currently selected one.
   * It's usually the ones displayed as multiple timepoints in the dashboard charts.
   * We also need currently selected brain part to identify the scan type to filter out low quality based on it.
   */
  @Selector([SubmissionState.submissions, SubmissionState.selectedSubmission, DashboardBrainPartsState.lastSelectedBrainPart])
  static relatedSubmissionScans(
    submissionsDict: {[p: string]: Submission},
    selectedSubmission: Submission,
    brainPart: DashboardBrainPartsStateModel['lastSelectedBrainPart'],
  ): Submission[] {
    // Wait until the selected submission is loaded.
    if (!selectedSubmission?.id) {
      return [];
    }

    // Get all accepted submissions as an array.
    const submissionList =
      // Transform stored dictionary to the list.
      Object.keys(submissionsDict)
        .map(key => submissionsDict[key])

        // Keep only good condition submissions.
        .filter(
          submission =>
            // Submission is approved.
            getScanStatusForScanType(submission, brainPart?.requiredScanType) === SubmissionDetailedStatus.approvedGeneral &&

            // Submission is not marked as bad quality (1 - the best quality, 5 - the worst).
            getScanQualityForScanType(submission, brainPart?.requiredScanType) <= acceptableScanQualityThreshold
        );

    // Return the related submissions based on the selected submission type.
    if (selectedSubmission.is_public) {
      return submissionList.filter(submission => submission.is_public);
    } else if (selectedSubmission.is_shared) {
      return submissionList.filter(submission => submission.is_shared && !submission.is_public);
    } else {
      return submissionList.filter(submission => !submission.is_shared && !submission.is_public);
    }
  }

  @Selector([
    DashboardBrainPartsState.brainPartSurfacesLoadingPriority,
    SubmissionState.selectedSubmission,
  ])
  static safeBrainPartSurfacesLoadingPriority(
    brainPartSurfacesLoadingPriority: DashboardBrainPartsStateModel['brainPartSurfacesLoadingPriority'],
    submission: Submission
  ): DashboardBrainPartsStateModel['brainPartSurfacesLoadingPriority'] {
    // Get all surface labels.
    const allSurfaceLabels = submission.surface_set.map(surface => surface.label_value);
    const flatSurfaceLabels = allSurfaceLabels.flat();

    // Filter the brain part surfaces loading priority to get only the surfaces that are in the submission.
    return (
      brainPartSurfacesLoadingPriority
        // Filter the arrays without surface ids.
        .filter(surfaceIdList => surfaceIdList?.length)

        // Filter the surface ids that are in the submission.
        .map(surfaceIdList => surfaceIdList.filter(surfaceId => flatSurfaceLabels.includes(surfaceId)))
    );
  }

  @Selector([DashboardBrainPartsState.brainPartListUnsafe, SubmissionState.selectedSubmission])
  static brainPartList(
    brainPartList: DashboardBrainPartsStateModel['brainPartList'],
    submission: Submission
  ): DashboardBrainPartsStateModel['brainPartList'] {
    // Submission is not fully loaded or is very old (cannot be handled by current brain part system and requires reprocessing).
    if (!submission?.version) {
      return [];
    }

    // Filter the brain part list root nodes to get only the brain parts that are present in the submission.
    // We want to hide only the version-incompatible brain parts, as scan-type-incompatible brain parts will be displayed as unavailable.
    const filteredRootBrainPartList = brainPartList?.filter(brainPart => this.isBrainPartCompatibleVersion(submission, brainPart));

    // Update the brain part availability for the root brain parts.
    const mappedRootBrainPartList = filteredRootBrainPartList.map(brainPart => this.updateBrainPartAvailability(submission, brainPart));

    // Remove child brain parts that are not presented in the submission.
    // We don't check the scan type compatibility as there are no such brain parts on the second or third level.
    // But it's possible to extend the logic in the future to filter them out or highlight them as unavailable.
    return mappedRootBrainPartList.map(brainPart => {
      return {
        ...brainPart,
        childRegions: brainPart.childRegions

          // Remove second level child brain parts that are not presented in the submission.
          .filter(child => this.isBrainPartCompatibleVersion(submission, child))

          // Remove third level child brain parts that are not presented in the submission.
          .map(grandChild => {
            return {
              ...grandChild,
              childRegions: grandChild.childRegions?.filter(grandchild => this.isBrainPartCompatibleVersion(submission, grandchild)),
            };
          }),
      };
    });
  }

  /**
   * Contains the list of the presented brain part ids for the current submission.
   */
  @Selector([DashboardBrainPartsState.brainPartsFlatListMap, SubmissionState.selectedSubmission])
  static selectableBrainPartIdList(
    brainPartsFlatListMap: DashboardBrainPartsStateModel['brainPartsFlatListMap'],
    submission: Submission,
  ): DashboardBrainPart['id'][] {
    // Create a new map to store the selectable brain parts flat list.
    const selectableBrainPartIdList: DashboardBrainPart['id'][] = [];

    // Check if the brain parts flat list map is not an array.
    if (!brainPartsFlatListMap) {
      return selectableBrainPartIdList;
    }

    // Iterate over the brain parts flat list map.
    brainPartsFlatListMap.forEach((brainPart, brainPartId) => {
      // Check if the brain part is presented in the submission.
      if (this.isBrainPartSelectable(submission, brainPart)) {
        selectableBrainPartIdList.push(brainPartId);
      }
    });

    // Return the selectable brain parts flat list map.
    return selectableBrainPartIdList;
  }

  /**
   * Returns the related scan quality for the last selected brain part and the selected submission.
   */
  @Selector([DashboardBrainPartsState.lastSelectedBrainPart, SubmissionState.selectedSubmission])
  static relatedScanQuality(
    brainPart: DashboardBrainPartsStateModel['lastSelectedBrainPart'],
    submission: Submission,
  ): SubmissionScanQuality {
    // If some data is not available yet, consider it as a good quality scan to avoid flickering.
    if (!submission?.base_image || !brainPart?.id) {
      return SubmissionScanQuality.best;
    }

    // Extract the required scan type for the brain part.
    return getScanQualityForScanType(submission, brainPart.requiredScanType);
  }

  /**
   * Defines if the brain part is presented in the submission and can be selected.
   * The region can be missed for some old derivatives, so compare the submission version with the required version.
   * It also can be missed if the related scan is not approved (or not present).
   */
  static isBrainPartSelectable(submission: Submission, brainPart: DashboardBrainPartSecondaryLevel): boolean {
    switch (brainPart.requiredScanType) {
      // The T1 scan is related to the Key Layer derivative.
      // Define the T1 related brain parts by comparing the submission Key Layer version with the required version.
      case SubmissionScanImageType.t1:
        const compatibleKeyLayerVersions = brainPart?.compatibleKeyLayerVersions?.map(version => version.id);
        return compatibleKeyLayerVersions?.includes(submission?.version);

      // For all other scan types, check if the related scan is present in the submission.
      // We do not need versions for the other scan types yet, so we simply check if the related scan is present.
      case SubmissionScanImageType.t2:
        return submission?.related_scans_approved?.T2?.status_description === SubmissionDetailedStatus.approvedGeneral;
      case SubmissionScanImageType.flair:
        return submission?.related_scans_approved?.Flair?.status_description === SubmissionDetailedStatus.approvedGeneral;
      case SubmissionScanImageType.tof:
        return submission?.related_scans_approved?.TOF?.status_description === SubmissionDetailedStatus.approvedGeneral;
      case SubmissionScanImageType.swi:
        return submission?.related_scans_approved?.SWI?.status_description === SubmissionDetailedStatus.approvedGeneral;
    }
    return true;
  }

  /**
   * Defines if the brain part is compatible with the submission version.
   */
  static isBrainPartCompatibleVersion(submission: Submission, brainPart: DashboardBrainPartSecondaryLevel): boolean {
    switch (brainPart.requiredScanType) {
      // The T1 scan is related to the Key Layer derivative.
      // Define the T1 related brain parts by comparing the submission Key Layer version with the required version.
      case SubmissionScanImageType.t1:
        const compatibleKeyLayerVersions = brainPart?.compatibleKeyLayerVersions?.map(version => version.id);
        return compatibleKeyLayerVersions?.includes(submission?.version);

      // All other scan types don't have a version dependency.
      // Mark all of them as presented; they can be unavailable in the submission if the related scan is not approved.
      // But we still want to display them in the carousel to show the user what is missing.
    }
    return true;
  }

  /**
   * Add the unavailable details to the brain part if needed.
   */
  static updateBrainPartAvailability<T extends DashboardBrainPartSecondaryLevel>(submission: Submission, brainPart: T): T {
    switch (brainPart.requiredScanType) {
      // The T1 scan is required and should always be present in the submission.
      // So, we consider all the T1 related brain parts as available.
      case SubmissionScanImageType.t1:
        return brainPart;

      // For all other scan types, check if the related scan is approved in the submission.
      case SubmissionScanImageType.t2:
        if (submission?.related_scans_approved?.T2?.status_description !== SubmissionDetailedStatus.approvedGeneral) {
          return {
            ...brainPart,
            isUnavailable: true,
            unavailableReason: `Require T2. Current status: ${submissionDetailedStatusToUserMessage(submission?.related_scans_approved?.T2?.status_description)}`,
          };
        }
        return brainPart;
      case SubmissionScanImageType.flair:
        if (submission?.related_scans_approved?.Flair?.status_description !== SubmissionDetailedStatus.approvedGeneral) {
          return {
            ...brainPart,
            isUnavailable: true,
            unavailableReason: `Require FLAIR. Current status: ${submissionDetailedStatusToUserMessage(submission?.related_scans_approved?.Flair?.status_description)}`,
          };
        }
        return brainPart;
      case SubmissionScanImageType.tof:
        if (submission?.related_scans_approved?.TOF?.status_description !== SubmissionDetailedStatus.approvedGeneral) {
          return {
            ...brainPart,
            isUnavailable: true,
            unavailableReason: `Require TOF. Current status: ${submissionDetailedStatusToUserMessage(submission?.related_scans_approved?.TOF?.status_description)}`,
          };
        }
        return brainPart;
      case SubmissionScanImageType.swi:
        if (submission?.related_scans_approved?.SWI?.status_description !== SubmissionDetailedStatus.approvedGeneral) {
          return {
            ...brainPart,
            isUnavailable: true,
            unavailableReason: `Require SWI. Current status: ${submissionDetailedStatusToUserMessage(submission?.related_scans_approved?.SWI?.status_description)}`,
          };
        }
        return brainPart;
    }
  }
}
